import * as React from "react";
import { AddCircle16Regular, Delete16Regular } from "@fluentui/react-icons";
import { updateEditableFields } from "../../../../redux/reducers/RepertoireReducer";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { 
    CANCEL_BUTTON_TEXT_FIELD,
    SAVE_BUTTON_TEXT_FIELD,
    USAGE_DESCRIPTION_EDITABLE_VIEW,
    USAGE_MAINTENANCE_GENERAL_DATA_VIEW,
    USAGE_PAGE_VIEW
 } from "../../../Consts";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import TextDataInput, { SizedTextDataInput } from "../../textDataInput/TextDataInput";
import { SizedDropdownDataInput } from "../../dropdownDataInput/DropdownDataInput";
import IconTextButton from "../../iconTextButton/IconTextButton";
import { IComponentView } from "../../../../redux/types/ComponentView";
import EditableTableSettings from "./EditableTableSettings";

import {
  deepClone,
  createNewView,
  getUniqueViewName,
  getUniqueViewNameFromRename,
  DEFAULT_VIEW_NAME,
  mergeFieldsWithSeededFields,
} from "./utils/usageResultsFieldUtils";
import { IComponentViewsPreference } from "../../../../redux/types/IComponentViewsPreference";
import { ColumnsType } from "antd/es/table";
import { Table } from "antd/lib";
import { ToggleButtonDataInput } from "../../toggleInput/ToggleInput";
export interface IFieldSettingViewModalProps {
    tableContents: any;
    componentViewData: any;
    componentInstance: string;
    dataGridViewData: IRepertoireComponentDataItem;
    updateEditableFields: typeof updateEditableFields;
    onClickSave: () => void;
    onClickCancel: () => void;
    editableFieldsKey?: string;
    componentViews: IComponentView[];
    activeComponentView?: IComponentView;
    setActiveComponentView?: (componentView: IComponentView) => void;
    updateComponentViews: (componentViews: IComponentView[]) => void;
    getComponentViews?: (componentName: string, componentInstance: string) => void;
    componentViewPreference?: IComponentViewsPreference;
    setIsFormDirty: (isDirty: boolean) => void;
    usageType?: string;
}

const SELECT_VIEW_TO_CONFIGURE = "SelectViewToConfigure";
const RENAME_SELECTED_VIEW = "RenameSelectedView";
interface optionType {
        code: string;
        description: string;
        extraFields?: object;
    }

const UsageMaintenanceFieldSettingView: React.FC<IFieldSettingViewModalProps> = ({
    getComponentViews,
    tableContents,
    componentViewData,
    componentInstance,
    dataGridViewData,
    updateEditableFields,
    onClickSave,
    onClickCancel,
    editableFieldsKey,
    componentViews,
    updateComponentViews,
    activeComponentView,
    setActiveComponentView,
    componentViewPreference,
    setIsFormDirty,
    usageType,
}) => {
const [loaded, setLoaded] = React.useState(false);
const [viewData, setViewData] = React.useState({});
const [localComponentViewsData, setComponentViewsData] = React.useState<IComponentView[]>([]);
const [viewsOptions, setViewsOptions] = React.useState<optionType[]>([]);
const [activeView, setActiveView] = React.useState<string | null>(componentViewPreference?.usageMaintenanceGeneralDataView || DEFAULT_VIEW_NAME);
const [pendingViewName, setPendingViewName] = React.useState<string>(activeView || '');
const [tableContent, setTableContents] = React.useState<IRepertoireField[]>(tableContents);


const initializeViewData = () => {
  if (componentViewData?.fields) {
    const newViewData = componentViewData.fields.reduce((acc, item) => {
      acc[item.name] = item.data;
      return acc;
    }, {} as Record<string, any>);
    if (componentViewPreference) {
      setActiveView(componentViewPreference.usageMaintenanceGeneralDataView);
    setPendingViewName(componentViewPreference.usageMaintenanceGeneralDataView);
    }
    setViewData(newViewData);
    setLoaded(true);
  }
};


React.useEffect(() => {
    if(!loaded) {
       initializeViewData();
    }
}, [tableContents])


React.useEffect(() => {
    if (activeComponentView) {
        setActiveView(activeComponentView.viewName);
        setPendingViewName(activeComponentView.viewName);
    }
    createComponentsViews();
}, [componentViews, activeComponentView]);

React.useEffect(() => {
  if (!activeView) return;

  const currentView = localComponentViewsData.find(
    v => v.viewName === activeView && !v.isDeleted
  );

  if (currentView) {
    setTableContents(currentView.fields);
  }
}, [localComponentViewsData, activeView]);

const getViewsOptions = (views: IComponentView[]) => {
  const filteredAndSortedViews = views
   .filter(view =>
  view.componentInstance === USAGE_PAGE_VIEW &&
  view.componentName === USAGE_MAINTENANCE_GENERAL_DATA_VIEW &&
  view.isDeleted !== true
)
    .sort((a, b) => a.viewName.localeCompare(b.viewName)); 

  const options: optionType[] = filteredAndSortedViews.map(view => ({
    code: view.viewName,
    description: view.viewName,
  }));

  setViewsOptions(options);
};

const handleViewNameDataChange = (value: string | boolean | number, fieldName: IRepertoireStateKeys) => {
  setIsFormDirty(true);
  if (typeof value === 'string') {
    if (value.trim() === '') {
      value = getUniqueViewName(localComponentViewsData, value);
    }
    setPendingViewName(value); 
  }
};

const initializeFields = (fields: IRepertoireField[]) => {
    return fields.map(field => ({
        ...field,
        hidden: field.hidden ?? false,
    }));
};


const createComponentsViews = () => {

    const seededFields = initializeFields(tableContents);

    if (!componentViews?.length) {

        const defaultEmptyView: IComponentView = createNewView(
            DEFAULT_VIEW_NAME,
            USAGE_MAINTENANCE_GENERAL_DATA_VIEW,
            USAGE_PAGE_VIEW,
            seededFields
        );

        const newEmptyView: IComponentView = createNewView(
            getUniqueViewName(localComponentViewsData),
            USAGE_MAINTENANCE_GENERAL_DATA_VIEW,
            USAGE_PAGE_VIEW,
            seededFields
        );

        const defaultEmptyViews = [defaultEmptyView, newEmptyView];

        getViewsOptions(defaultEmptyViews);
        setComponentViewsData(deepClone(defaultEmptyViews));
        setTableContents(
            deepClone(defaultEmptyView.fields)
        );

    } else {

        const normalizedViews = componentViews.map(view => ({
            ...view,
            fields: mergeFieldsWithSeededFields(
                view.fields || [],
                tableContents
            ),
        }));

        getViewsOptions(normalizedViews);

        setComponentViewsData(deepClone(normalizedViews));

        const activeViewFields =
            normalizedViews.find(
                item => item.viewName === activeView
            )?.fields || [];

        setTableContents(
            deepClone(activeViewFields)
        );
    }
};

    const handleSaveClick = () => {
        setIsFormDirty(false);
        updateComponentViews(localComponentViewsData);
        onClickSave();
    }

   const renderActionButtons = () => {
        return (
            <div >
                <div className="form-group col-lg-3 col-lg-push-6 col-md-4 col-md-push-4 col-sm-6 col-sm-push-0 col-xs-6 col-xs-push-0">
                    <button className="button btn-defaultSecondary" onClick={onClickCancel}>
                        {viewData[CANCEL_BUTTON_TEXT_FIELD]}
                    </button>
                </div>

                <div className="form-group col-lg-3 col-lg-push-6 col-md-4 col-md-push-4 col-sm-6 col-sm-push-0 col-xs-6 col-xs-push-0">
                    <button className="button btn-defaultPrimary" onClick={handleSaveClick}>
                        {viewData[SAVE_BUTTON_TEXT_FIELD]}
                    </button>
                </div>
            </div>
        )
    }


const handleViewDropdownDataChange = (
    value: string | string[] | boolean,
    fieldName: IRepertoireStateKeys,
) => {
    setIsFormDirty(true);

    const selectedViewName = value as string;

    setActiveView(selectedViewName);
    setPendingViewName(selectedViewName);

    const selectedView = localComponentViewsData.find(
        item =>
            item.viewName === selectedViewName &&
            item.viewName !== DEFAULT_VIEW_NAME
    );

    const mergedFields = mergeFieldsWithSeededFields(
        selectedView?.fields || [],
        tableContents
    );

    setTableContents(deepClone(mergedFields));
};

    const handleTextFieldChange = (value: any, name: IRepertoireStateKeys) => {
       setIsFormDirty(true);
          setComponentViewsData(prevState => {
            const updatedViews = prevState.map(item => {
                if (item.viewName === activeView) {
                    return {...item, fields: [...item.fields.map(field => field.name === name ? {...field, data: value} : field)]};
                }
                return item;
            });
            return updatedViews;
        });
    }

const handleToggleChange = (value: boolean, name: IRepertoireStateKeys) => {
    setIsFormDirty(true);

    setComponentViewsData(prevState => {
        return prevState.map(item => {
            if (item.viewName === activeView) {
                return {
                    ...item,
                    fields: item.fields.map(field =>
                        field.name === name
                            ? { ...field, hidden: !field.hidden }
                            : field
                    ),
                };
            }

            return item;
        });
    });
};


const confirmRename = () => {
  if (!pendingViewName || pendingViewName === activeView) return;

  const nameWithSuffixIfItAlreadyThere = getUniqueViewNameFromRename(pendingViewName, activeView, localComponentViewsData, componentViews);

  setComponentViewsData(prev =>
    prev.map(view =>
      view.viewName === activeView ? { ...view, viewName: nameWithSuffixIfItAlreadyThere } : view
    )
  );

  setViewsOptions(prev =>
    prev.map(opt =>
      opt.code === activeView ? {code: nameWithSuffixIfItAlreadyThere, description: nameWithSuffixIfItAlreadyThere } : opt
    )
  );

  setActiveView(nameWithSuffixIfItAlreadyThere);
};

const handleDeleteView = () => {
    setIsFormDirty(true);
  if (!activeView) return;

  if (activeView === DEFAULT_VIEW_NAME) {
    return;
  }

  const newUpdatedViews = localComponentViewsData.map((view, index) => {
    if (view.viewName === activeView && view.viewName !== DEFAULT_VIEW_NAME) {
    view.isDeleted = true;
  }
  return view;  
   });

  const fallbackView = newUpdatedViews.length > 0 ? newUpdatedViews[0].viewName : DEFAULT_VIEW_NAME;

  setComponentViewsData(newUpdatedViews);
  getViewsOptions(newUpdatedViews)
  setActiveView(fallbackView);
  setPendingViewName(fallbackView);

  const fallbackFields = newUpdatedViews.find(v => v.viewName === fallbackView)?.fields || [];
  setTableContents(fallbackFields);
};

    let columnsTop: ColumnsType<any> = [
  {
    title: "Field Labels",
    dataIndex: "fieldLabels",
    key: "fieldLabels",
    render: (value, record, index) => {
      return      <TextDataInput
                        value={value}
                        fieldName={'fieldLabels'}
                        changeData={(value, stateKey) => { handleTextFieldChange(value as string, record.fieldName)}}
                        readOnly={activeView === DEFAULT_VIEW_NAME}
                        isHidden={false}
                        objectKey={'fieldLabels'}
                              /> 
    },
    width: "5%",
  },
  {
    title: "Off / On",
    dataIndex: "isHiddenField",
    key: "isHiddenField",
    render: (value, record, index) => {
         const fieldName = record.originalField.name;

    const handleChange = (changedValue) => {
        handleToggleChange(changedValue, fieldName);
    };

    return (
        <ToggleButtonDataInput
            changeData={handleChange}
            fieldName={`isHiddenField ${record.key}`}
            value={value}
            readonly={activeView === DEFAULT_VIEW_NAME}
        />
    );
    },
    width: "10%",
  },
];



const handleAddNewView = () => {
    if (!localComponentViewsData || localComponentViewsData.length === 0) return;
    const newView: IComponentView = createNewView(getUniqueViewName(localComponentViewsData), USAGE_MAINTENANCE_GENERAL_DATA_VIEW, USAGE_PAGE_VIEW, tableContents); 
    setComponentViewsData(prev => [...prev, newView]);
    setViewsOptions(prev => [...prev, { code: newView.viewName, description: newView.viewName }]);
    setActiveView(newView.viewName);
    setPendingViewName(newView.viewName);
    setTableContents(newView.fields);
}
  const data = (activeView === DEFAULT_VIEW_NAME ? tableContents : tableContent).filter(
      field => field.componentInstance === usageType
    ).sort((a, b) => a.name.localeCompare(b.name));

const buildTableRows = (fields: IRepertoireField[]) => {
  return fields.map((field, index) => ({
    key: `${field.name}_${index}`,
    originalField: field,
    fieldName: field.name,
    fieldLabels: field.data,
    defaultValue: field?.defaultValue || "",
    isHiddenField: field?.hidden,
    isMandatory: field?.isMandatory,
    isModifiable: field?.isMandatoryModifiable !== false,
  }));
};


        if (loaded) {
        return (
            <div>
                <div className="flexRow paddingLeftLarge">
                              <SizedTextDataInput
                                    label={componentViewData?.fields?.find(item => item.name === RENAME_SELECTED_VIEW)?.data ? componentViewData?.fields?.find(item => item.name === RENAME_SELECTED_VIEW)?.data : 'Rename selected view'}
                                    isHidden={false}
                                    changeData={handleViewNameDataChange}
                                    fieldName="usageDescription"
                                    onBlurEvent={confirmRename}
                                    isOnBlurOn={true}
                                    value={pendingViewName}
                                    readOnly={activeView === DEFAULT_VIEW_NAME}
                                    handleKeyDown={(e) => e.key === 'Enter' && confirmRename()}
                                               />
                              <SizedDropdownDataInput
                                    options={viewsOptions}
                                    label={componentViewData.fields.find(item => item.name === SELECT_VIEW_TO_CONFIGURE)?.data || 'Select View to Configure'}
                                    changeData={handleViewDropdownDataChange}
                                    value={activeView ? activeView : DEFAULT_VIEW_NAME}
                                    fieldName={SELECT_VIEW_TO_CONFIGURE}
                                    componentInstance={componentInstance}
                                    isHidden={false}
                                    isMandatory={false}
                                    backendSearch={false}
                                    allowNull={false}
                                    allowFreeInput={false}
                              />  
                              <div className="flexRow paddingRightLarge">  
                             <IconTextButton 
                                disabled={activeView === DEFAULT_VIEW_NAME} 
                                onClick={handleDeleteView} 
                                text={'Delete'} 
                                icon={Delete16Regular}
                              />
                               </div>
                            <IconTextButton 
                                disabled={false} 
                                onClick={handleAddNewView} 
                                text={'Add'} 
                                icon={AddCircle16Regular}
                              />
                             
                </div>
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <p>{viewData[USAGE_DESCRIPTION_EDITABLE_VIEW]}</p>
                </div>
                        <div style={{ maxWidth: "800px", margin: "0 auto", overflow: "clip"
                         }}>
                        <Table
                            rowKey={(record) => record.key}
                            columns={columnsTop}
                            dataSource={buildTableRows(tableContent.filter(field => field.componentInstance === usageType).sort((a, b) => a.name.localeCompare(b.name)))}                                                            
                            rowClassName={() => 'editable-row'}
                            size={'middle'}
                            onChange={() => { } }
                            className="parent-table"
                            style={{ width: "100%" }}
                            pagination={false}
                        />
                        </div>

                {renderActionButtons()}
            </div>
            );
        }
        else {
            return <div>Loading...</div>
        }

};

export default UsageMaintenanceFieldSettingView;
